import { PlusOutlined } from "@ant-design/icons";
import type { GetProp, UploadFile, UploadProps } from "antd";
import {
  Button,
  DatePicker,
  Divider,
  Flex,
  Image,
  Input,
  Typography,
  Upload,
} from "antd";
// Import useRef from React
import TextArea from "antd/es/input/TextArea";
import dayjs, { Dayjs } from "dayjs";
import isSameOrNull from "dayjs/plugin/isSameOrAfter";
import PubSub from "pubsub-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useToken } from "../../hooks/useToken";
import { TMedia } from "../../models/media.model";
import { useTheme } from "../../providers/ThemeProvider";
import { useAuth } from "../../providers/UserProvider";
import { getMediaFile, uploadMedia } from "../../services/media.service";
import {
  getUserProfile,
  TProfile,
  updateUserProfileFields,
} from "../../services/profile.service";

dayjs.extend(isSameOrNull);

const { Title, Text } = Typography;

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

interface UserFormData {
  displayName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: Dayjs | null;
  bio: string;
  address1: string;
  address2: string;
  state: string;
  country: string;
}

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export function AccountBox() {
  const { token } = useToken();
  const { user } = useAuth();
  const { message: themeMessage } = useTheme();
  // Ref para o DIV que vai envolver a seção de upload
  const uploadWrapperRef = useRef<HTMLDivElement>(null);

  const beforeUpload = useCallback(
    (file: FileType) => {
      const isJpgOrPng =
        file.type === "image/jpeg" || file.type === "image/png";
      if (!isJpgOrPng) {
        themeMessage.error("You can only upload JPG/PNG files!");
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        themeMessage.error("Image must be smaller than 2MB!");
        return false;
      }
      return false;
    },
    [themeMessage]
  );

  const initialFormData: UserFormData = {
    displayName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: null,
    bio: "",
    address1: "",
    address2: "",
    state: "",
    country: "",
  };

  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [photoURL, setPhotoURL] = useState<string | undefined>(undefined);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [profileDataSource, setProfileDataSource] = useState<TProfile | null>(
    null
  );
  const [initialLoadedData, setInitialLoadedData] = useState<{
    formData: UserFormData;
    fileList: UploadFile[];
  } | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const handleFormChange = (
    field: keyof UserFormData,
    value: string | Dayjs | null
  ) => {
    setFormData((prevData) => ({ ...prevData, [field]: value }));
  };

  const loadProfileData = useCallback(
    (currentUser: typeof user, currentPhotoURL?: string) => {
      if (!currentUser || !currentUser.uid) {
        setIsLoading(false);
        setInitialLoadedData(null);
        return;
      }
      setIsLoading(true);
      setIsDirty(false);
      setInitialLoadedData(null);

      getUserProfile({
        onSuccess: (profile) => {
          setProfileDataSource(profile);
          const loadedFormData: UserFormData = {
            displayName: profile.name ?? currentUser.displayName ?? "",
            email: currentUser.email ?? "",
            phoneNumber: profile.phoneNumber ?? currentUser.phoneNumber ?? "",
            dateOfBirth: profile.birthday ? dayjs(profile.birthday) : null,
            bio: profile.bio ?? "",
            address1: profile.address1 ?? "",
            address2: profile.address2 ?? "",
            state: profile.state ?? "",
            country: profile.country ?? "",
          };
          setFormData(loadedFormData);

          if (profile.avatar) {
            console.log(
              `[Load Data] Profile has avatar ID: ${profile.avatar}. Fetching URL...`
            );
            getMediaFile({
              mediaId: profile.avatar,
              onSuccess: (data) => {
                const validUrl = URL.createObjectURL(data);
                console.log(`[Load Data] Received URL: ${validUrl}`);
                setPhotoURL(validUrl);
                const loadedFileList: UploadFile[] = validUrl
                  ? [
                      {
                        uid: "-1",
                        name: "avatar.png",
                        status: "done" as const,
                        url: validUrl,
                        thumbUrl: validUrl,
                      },
                    ]
                  : [];
                setFileList(loadedFileList);
                setInitialLoadedData({
                  formData: loadedFormData,
                  fileList: loadedFileList,
                });
                setIsLoading(false);
              },
              onError: (err) => {
                console.error(
                  "[Load Data] Failed to fetch initial avatar URL:",
                  err
                );
                setPhotoURL(currentPhotoURL);
                const loadedFileList: UploadFile[] = currentPhotoURL
                  ? [
                      {
                        uid: "-1",
                        name: "avatar.png",
                        status: "done" as const,
                        url: currentPhotoURL,
                        thumbUrl: currentPhotoURL,
                      },
                    ]
                  : [];
                setFileList(loadedFileList);
                setInitialLoadedData({
                  formData: loadedFormData,
                  fileList: loadedFileList,
                });
                setIsLoading(false);
              },
            });
          } else {
            console.log(
              "[Load Data] Profile has no avatar ID. Using Auth photoURL if available."
            );
            setPhotoURL(currentPhotoURL);
            const loadedFileList: UploadFile[] = currentPhotoURL
              ? [
                  {
                    uid: "-1",
                    name: "avatar.png",
                    status: "done" as const,
                    url: currentPhotoURL,
                    thumbUrl: currentPhotoURL,
                  },
                ]
              : [];
            setFileList(loadedFileList);
            setInitialLoadedData({
              formData: loadedFormData,
              fileList: loadedFileList,
            });
            setIsLoading(false);
          }
        },
        onError: (error) => {
          console.error("Error fetching Firestore profile:", error);
          const fallbackFormData: UserFormData = {
            displayName: currentUser.displayName ?? "",
            email: currentUser.email ?? "",
            phoneNumber: currentUser.phoneNumber ?? "",
            dateOfBirth: null,
            bio: "",
            address1: "",
            address2: "",
            state: "",
            country: "",
          };
          setFormData(fallbackFormData);
          setPhotoURL(currentPhotoURL);
          const fallbackFileList: UploadFile[] = currentPhotoURL
            ? [
                {
                  uid: "-1",
                  name: "avatar.png",
                  status: "done" as const,
                  url: currentPhotoURL,
                  thumbUrl: currentPhotoURL,
                },
              ]
            : [];
          setFileList(fallbackFileList);
          setProfileDataSource(null);
          setInitialLoadedData({
            formData: fallbackFormData,
            fileList: fallbackFileList,
          });
          setIsLoading(false);
          themeMessage.error("Failed to load profile details.");
        },
      });
    },
    [themeMessage]
  );

  useEffect(() => {
    if (user) {
      const originalPhotoURL = user.photoURL ?? undefined;
      loadProfileData(user, originalPhotoURL);
    } else {
      setFormData(initialFormData);
      setPhotoURL(undefined);
      setFileList([]);
      setIsLoading(false);
      setProfileDataSource(null);
      setInitialLoadedData(null);
      setIsDirty(false);
    }
  }, [user, loadProfileData]);

  useEffect(() => {
    if (!initialLoadedData || isLoading) {
      setIsDirty(false);
      return;
    }
    let formChanged = false;
    for (const key in formData) {
      if (key === "dateOfBirth") {
        const currentVal = formData[key];
        const initialVal = initialLoadedData.formData[key];
        if (
          (currentVal === null && initialVal !== null) ||
          (currentVal !== null && initialVal === null)
        ) {
          formChanged = true;
          break;
        }
        if (currentVal && initialVal && !currentVal.isSame(initialVal, "day")) {
          formChanged = true;
          break;
        }
      } else if (
        formData[key as keyof UserFormData] !==
        initialLoadedData.formData[key as keyof UserFormData]
      ) {
        formChanged = true;
        break;
      }
    }
    const initialHasFile = initialLoadedData.fileList.length > 0;
    const currentHasFile = fileList.length > 0;
    const currentFileIsNew = currentHasFile && !!fileList[0].originFileObj;
    const fileWasRemoved = initialHasFile && !currentHasFile;
    const avatarChanged = currentFileIsNew || fileWasRemoved;
    setIsDirty(formChanged || avatarChanged);
  }, [formData, fileList, initialLoadedData, isLoading]);

  const handlePreview = async (file: UploadFile) => {
    let src = file.url || file.thumbUrl;
    if (!src && file.originFileObj) {
      try {
        src = await getBase64(file.originFileObj as FileType);
      } catch (error) {
        themeMessage.error("Could not generate image preview.");
        return;
      }
    }
    if (!src) {
      themeMessage.error("Could not find URL or preview for the image.");
      return;
    }
    setPreviewImageUrl(src);
    setPreviewVisible(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(-1));
  };

  const handleRemove = (file: UploadFile): boolean | Promise<boolean> => {
    return true;
  };

  const handleSaveChanges = async () => {
    const uid = user?.uid || localStorage.getItem("uid");
    if (!uid) {
      themeMessage.error("User not authenticated.");
      return;
    }
    setIsSaving(true);
    console.log("[Save Start] Initiating save process...");
    const currentFile = fileList.length > 0 ? fileList[0] : null;
    let uploadedMediaInfo: TMedia | null = null;
    let errorOccurred = false;
    let newAvatarId: string | null | undefined = undefined;
    if (currentFile && currentFile.originFileObj) {
      console.log(
        "[Save Upload] New file detected, attempting upload:",
        currentFile.name
      );
      try {
        uploadedMediaInfo = await new Promise<TMedia>((resolve, reject) => {
          uploadMedia({
            file: currentFile.originFileObj as FileType,
            onSuccess: resolve,
            onError: reject,
          });
        });
        console.log("[Save Upload] Upload successful:", uploadedMediaInfo);
        newAvatarId = uploadedMediaInfo.id;
      } catch (err) {
        console.error("[Save Upload] Failed to upload media:", err);
        themeMessage.error("Failed to upload new avatar. Please try again.");
        errorOccurred = true;
      }
    } else if (
      fileList.length === 0 &&
      initialLoadedData &&
      initialLoadedData.fileList.length > 0
    ) {
      console.log("[Save Upload] Avatar removal detected.");
      newAvatarId = null;
    } else {
      console.log("[Save Upload] No new file selected or avatar unchanged.");
    }
    if (errorOccurred) {
      setIsSaving(false);
      if (initialLoadedData?.fileList) {
        setFileList(initialLoadedData.fileList);
      } else if (photoURL) {
        setFileList([
          {
            uid: "-1",
            name: "avatar.png",
            status: "done" as const,
            url: photoURL,
            thumbUrl: photoURL,
          },
        ]);
      } else {
        setFileList([]);
      }
      return;
    }
    const dataToSave: Partial<TProfile> = {
      name: formData.displayName,
      phoneNumber: formData.phoneNumber || null,
      birthday: formData.dateOfBirth
        ? formData.dateOfBirth.format("YYYY-MM-DD")
        : null,
      bio: formData.bio || null,
      address1: formData.address1 || null,
      address2: formData.address2 || null,
      state: formData.state || null,
      country: formData.country || null,
    };
    let avatarChangeDescription = "No change";
    if (newAvatarId !== undefined) {
      dataToSave.avatar = newAvatarId;
      avatarChangeDescription =
        newAvatarId === null
          ? "Avatar removed"
          : `New avatar ID: ${newAvatarId}`;
    }
    console.log(
      "[Save Data] Preparing data for update:",
      dataToSave,
      `(Avatar change: ${avatarChangeDescription})`
    );
    delete (dataToSave as any).id;
    const hasFormChanges = Object.keys(dataToSave).some(
      (key) => key !== "avatar"
    );
    const hasAvatarChange = newAvatarId !== undefined;
    if (hasFormChanges || hasAvatarChange) {
      console.log("[Save Update] Calling updateUserProfileFields...");
      try {
        const updatedProfile = await new Promise<TProfile>(
          (resolve, reject) => {
            updateUserProfileFields({
              data: dataToSave,
              onSuccess: resolve,
              onError: reject,
            });
          }
        );
        themeMessage.success("Changes saved successfully!");
        console.log("[Save Success] Updated Profile Data:", updatedProfile);
        let displayAvatarUrl: string | undefined = undefined;
        let errorFetchingUrl = false;
        if (
          updatedProfile.avatar &&
          typeof updatedProfile.avatar === "string"
        ) {
          console.log(
            `[Save Success] Profile has avatar ID: ${updatedProfile.avatar}. Fetching URL via getMediaFile...`
          );
          try {
            displayAvatarUrl = await new Promise<string>((resolve, reject) => {
              getMediaFile({
                mediaId: updatedProfile.avatar as string,
                onSuccess: (data) => {
                  resolve(URL.createObjectURL(data));
                },
                onError: reject,
              });
            });
            console.log(
              `[Save Success] Received URL from getMediaFile: ${displayAvatarUrl}`
            );
          } catch (err) {
            console.error(
              "[Save Success][getMediaFile Error] Failed to fetch media URL:",
              err
            );
            themeMessage.error(
              "Profile saved, but could not display the updated avatar."
            );
            errorFetchingUrl = true;
          }
        } else {
          console.log(
            "[Save Success] No avatar ID in updated profile or avatar removed."
          );
          displayAvatarUrl = undefined;
        }
        setPhotoURL(displayAvatarUrl);
        setProfileDataSource(
          (prev) => ({ ...(prev ?? {}), ...updatedProfile } as TProfile)
        );
        const savedFileList: UploadFile[] =
          displayAvatarUrl && !errorFetchingUrl
            ? [
                {
                  uid: Date.now().toString(),
                  name: "avatar.png",
                  status: "done" as const,
                  url: displayAvatarUrl,
                  thumbUrl: displayAvatarUrl,
                },
              ]
            : [];
        setFileList(savedFileList);
        const finalFormDataState = {
          displayName: updatedProfile.name ?? "",
          email: user?.email ?? "",
          phoneNumber: updatedProfile.phoneNumber ?? "",
          dateOfBirth: updatedProfile.birthday
            ? dayjs(updatedProfile.birthday)
            : null,
          bio: updatedProfile.bio ?? "",
          address1: updatedProfile.address1 ?? "",
          address2: updatedProfile.address2 ?? "",
          state: updatedProfile.state ?? "",
          country: updatedProfile.country ?? "",
        };
        setFormData(finalFormDataState);
        setInitialLoadedData({
          formData: finalFormDataState,
          fileList: savedFileList,
        });
        setIsDirty(false);
        console.log("[Save Success] Publishing profileUpdate.");
        PubSub.publish("profileUpdate", updatedProfile);
      } catch (error) {
        console.error("[Save Error] Error saving profile:", error);
        themeMessage.error("Failed to save profile changes.");
      } finally {
        console.log("[Save Finally] Setting isSaving to false.");
        setIsSaving(false);
      }
    } else {
      console.log("[Save Update] No effective changes detected to save.");
      themeMessage.info("No changes detected to save.");
      setIsSaving(false);
    }
  };

  const handleCancelEditing = () => {
    themeMessage.info("Editing cancelled.");
    if (user) {
      loadProfileData(user, user.photoURL ?? undefined);
    } else {
      setFormData(initialFormData);
      setPhotoURL(undefined);
      setFileList([]);
      setProfileDataSource(null);
      setInitialLoadedData(null);
      setIsDirty(false);
      setIsLoading(false);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none", padding: 0 }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return (
    <Flex vertical style={{ width: "100%", minHeight: "calc(100vh - 165px)" }}>
      <Flex
        vertical
        style={{
          width: "900px",
          maxWidth: "100%",
          margin: "0 auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
        gap={token.margin}
      >
        <Title level={4} style={{ marginBottom: 0, flexShrink: 0 }}>
          User Profile
        </Title>
        <Divider
          style={{ marginTop: 0, marginBottom: token.marginLG, flexShrink: 0 }}
        />

        {isLoading && <Text>Loading profile...</Text>}

        {!isLoading && (
          <>
            <Flex
              style={{
                width: "100%",
                gap: token.marginXL,
                flexGrow: 1,
                overflowY: "auto",
                paddingBottom: token.paddingLG,
                flexDirection: "row",
              }}
              align="flex-start"
            >
              <div ref={uploadWrapperRef}>
                <Flex
                  vertical
                  align="center"
                  gap={token.marginSM}
                  style={{ flexShrink: 0 }}
                >
                  <Upload
                    style={{
                      width: "120px",
                    }}
                    name="avatar"
                    listType="picture-circle"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={handleChange}
                    onRemove={handleRemove}
                    beforeUpload={beforeUpload}
                    customRequest={({ onSuccess }) => {
                      if (onSuccess)
                        setTimeout(() => {
                          onSuccess("ok");
                        }, 0);
                    }}
                    maxCount={1}
                    accept="image/png, image/jpeg"
                    disabled={isSaving}
                  >
                    {fileList.length < 1 ? uploadButton : null}
                  </Upload>
                  <Button
                    disabled={isSaving}
                    onClick={() => {
                      const inputElement =
                        uploadWrapperRef.current?.querySelector(
                          'input[type="file"]'
                        ) as HTMLInputElement | null;
                      inputElement?.click();
                    }}
                  >
                    Upload
                  </Button>
                </Flex>
              </div>

              <Flex
                vertical
                style={{ width: "100%", flexGrow: 1 }}
                gap={token.margin}
              >
                <Flex gap={token.margin} style={{ width: "100%" }}>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>Name</Text>
                    <Input
                      value={formData.displayName}
                      onChange={(e) =>
                        handleFormChange("displayName", e.target.value)
                      }
                      disabled={isLoading || isSaving}
                    />
                  </Flex>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>Date Of Birth</Text>
                    <DatePicker
                      minDate={dayjs("1900-01-01")} // Mantém a data mínima, se necessário
                      maxDate={dayjs().subtract(7, "year")}
                      style={{ width: "40%" }}
                      value={formData.dateOfBirth}
                      onChange={(date) => handleFormChange("dateOfBirth", date)}
                      format="YYYY-MM-DD"
                      disabled={isLoading || isSaving}
                    />
                  </Flex>
                </Flex>
                <Divider style={{ margin: "5px 0" }} />
                <Flex gap={token.margin} style={{ width: "100%" }}>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>Email</Text>
                    <Input disabled value={formData.email} />
                  </Flex>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>Phone Number</Text>
                    <Input
                      value={formData.phoneNumber}
                      onChange={(e) =>
                        handleFormChange("phoneNumber", e.target.value)
                      }
                      placeholder="(XX) XXXXX-XXXX"
                      disabled={isLoading || isSaving}
                    />
                  </Flex>
                </Flex>
                <Divider style={{ margin: "5px 0" }} />
                <Flex vertical gap={token.paddingXS} style={{ width: "100%" }}>
                  <Text>Bio</Text>
                  <TextArea
                    value={formData.bio}
                    onChange={(e) => handleFormChange("bio", e.target.value)}
                    rows={4}
                    disabled={isLoading || isSaving}
                  />
                </Flex>
                <Divider style={{ margin: "5px 0" }} />
                <Flex gap={token.margin} style={{ width: "100%" }}>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>Address Line 1</Text>
                    <Input
                      value={formData.address1}
                      onChange={(e) =>
                        handleFormChange("address1", e.target.value)
                      }
                      disabled={isLoading || isSaving}
                    />
                  </Flex>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>Address Line 2</Text>
                    <Input
                      value={formData.address2}
                      onChange={(e) =>
                        handleFormChange("address2", e.target.value)
                      }
                      disabled={isLoading || isSaving}
                    />
                  </Flex>
                </Flex>
                <Flex gap={token.margin} style={{ width: "100%" }}>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>City</Text>
                    <Input
                      value={formData.state}
                      onChange={(e) =>
                        handleFormChange("state", e.target.value)
                      }
                      disabled={isLoading || isSaving}
                    />
                  </Flex>
                  <Flex vertical gap={token.paddingXS} style={{ flex: 1 }}>
                    <Text>Country</Text>
                    <Input
                      value={formData.country}
                      onChange={(e) =>
                        handleFormChange("country", e.target.value)
                      }
                      disabled={isLoading || isSaving}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            <Flex
              justify="flex-end"
              gap={token.margin}
              style={{
                width: "100%",
                flexShrink: 0,
                marginTop: token.marginLG,
              }}
            >
              <Button
                onClick={handleCancelEditing}
                disabled={!isDirty || isSaving || isLoading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSaveChanges}
                loading={isSaving}
                disabled={!isDirty || isSaving || isLoading}
              >
                Save Changes
              </Button>
            </Flex>
          </>
        )}

        <div style={{ display: "none" }}>
          <Image.PreviewGroup
            preview={{
              visible: previewVisible,
              onVisibleChange: (vis) => setPreviewVisible(vis),
            }}
          >
            <Image src={previewImageUrl} />
          </Image.PreviewGroup>
        </div>
      </Flex>
    </Flex>
  );
}
